import { useEffect, useMemo, useRef, useState } from "react"
import { CheckCircle2, Loader2, Save, ShieldCheck } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  SETUP_SECTIONS,
  applySetupDefaults,
  getMissingRequired,
  getNestedValue,
  setNestedValue,
  setupApi,
  type SetupField,
  type SetupStatus,
} from "@/services/setup"

const LOCAL_DRAFT_KEY = "netzerp.setup.draft"
const LOCAL_TOKEN_KEY = "netzerp.setup.token"

function readLocalDraft(): Record<string, unknown> {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_DRAFT_KEY) || "{}")
  } catch {
    return {}
  }
}

function fieldInputType(field: SetupField): string {
  if (field.type === "email") return "email"
  if (field.type === "url") return "url"
  if (field.type === "number") return "number"
  if (field.type === "color") return "color"
  return "text"
}

export function SetupPage() {
  const urlToken = new URLSearchParams(window.location.search).get("token")
  const [setupToken, setSetupToken] = useState(() => urlToken || localStorage.getItem(LOCAL_TOKEN_KEY) || "")
  const [status, setStatus] = useState<SetupStatus | null>(null)
  const [draft, setDraft] = useState<Record<string, unknown>>(() => applySetupDefaults(readLocalDraft()))
  const [activeSection, setActiveSection] = useState(SETUP_SECTIONS[0]?.id ?? "organization")
  const [isSaving, setIsSaving] = useState(false)
  const [isCompleting, setIsCompleting] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const missingRequired = useMemo(() => getMissingRequired(draft), [draft])
  const active = SETUP_SECTIONS.find((section) => section.id === activeSection) ?? SETUP_SECTIONS[0]

  useEffect(() => {
    setupApi.status().then(setStatus).catch(() => setStatus(null))
  }, [])

  useEffect(() => {
    localStorage.setItem(LOCAL_DRAFT_KEY, JSON.stringify(draft))
    if (!setupToken) return

    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => {
      setIsSaving(true)
      setupApi
        .saveDraft(draft, setupToken)
        .then((response) => {
          setStatus((current) => current ? { ...current, state: response.state, draftExists: true } : current)
          setMessage("Entwurf gespeichert.")
        })
        .catch((error) => setMessage(error.message))
        .finally(() => setIsSaving(false))
    }, 900)

    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current)
    }
  }, [draft, setupToken])

  useEffect(() => {
    if (!setupToken) return
    localStorage.setItem(LOCAL_TOKEN_KEY, setupToken)
    setupApi
      .draft(setupToken)
      .then((response) => {
        if (Object.keys(response.draft).length > 0) {
          setDraft(applySetupDefaults(response.draft))
        }
      })
      .catch((error) => setMessage(error.message))
  }, [setupToken])

  const updateField = (field: SetupField, rawValue: string | boolean) => {
    const value = field.type === "number" ? Number(rawValue) : rawValue
    setDraft((current) => setNestedValue(current, field.key, value))
  }

  const saveNow = async () => {
    if (!setupToken) {
      setMessage("Setup-Token fehlt.")
      return
    }
    setIsSaving(true)
    try {
      await setupApi.saveDraft(draft, setupToken)
      setMessage("Entwurf gespeichert.")
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Speichern fehlgeschlagen.")
    } finally {
      setIsSaving(false)
    }
  }

  const complete = async () => {
    if (!setupToken) {
      setMessage("Setup-Token fehlt.")
      return
    }
    if (missingRequired.length > 0) {
      setMessage(`${missingRequired.length} Pflichtfelder fehlen.`)
      return
    }

    setIsCompleting(true)
    try {
      await setupApi.complete(draft, setupToken)
      localStorage.removeItem(LOCAL_DRAFT_KEY)
      setMessage("Initialisierung abgeschlossen.")
      window.location.href = "/login"
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Initialisierung fehlgeschlagen.")
    } finally {
      setIsCompleting(false)
    }
  }

  if (status && !status.requiresSetup) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#fbfcf8] p-6">
        <div className="w-full max-w-xl rounded-lg border border-[#dbe8c3] bg-white p-8 shadow-sm">
          <ShieldCheck className="mb-4 h-9 w-9 text-[#7fb000]" />
          <h1 className="text-2xl font-semibold text-[#151713]">Initialisierung abgeschlossen</h1>
          <p className="mt-2 text-sm text-[#657057]">Die Anwendung ist bereits eingerichtet.</p>
          <Button className="mt-6" onClick={() => (window.location.href = "/login")}>Zum Login</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#fbfcf8] text-[#151713]">
      <header className="border-b border-[#dbe8c3] bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#7fa52b]">NetzERP</p>
            <h1 className="mt-1 text-2xl font-semibold">Ersteinrichtung</h1>
          </div>
          <div className="flex items-center gap-3 text-sm text-[#657057]">
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            <span>{status?.state ?? "uninitialized"}</span>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-6 py-6 lg:grid-cols-[280px_1fr]">
        <aside className="space-y-3">
          <div className="rounded-lg border border-[#dbe8c3] bg-white p-4">
            <label className="text-sm font-medium">Setup-Token</label>
            <Input
              className="mt-2"
              value={setupToken}
              onChange={(event) => setSetupToken(event.target.value)}
              type="password"
              autoComplete="off"
            />
          </div>

          <nav className="rounded-lg border border-[#dbe8c3] bg-white p-2">
            {SETUP_SECTIONS.map((section) => {
              const sectionMissing = section.fields.filter((field) => missingRequired.includes(field.key)).length
              const isActive = section.id === activeSection
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setActiveSection(section.id)}
                  className={[
                    "flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm",
                    isActive ? "bg-[#eef7df] text-[#5d8500]" : "text-[#3b4035] hover:bg-[#f6f9ef]",
                  ].join(" ")}
                >
                  <span>{section.title}</span>
                  {sectionMissing === 0 ? (
                    <CheckCircle2 className="h-4 w-4 text-[#7fb000]" />
                  ) : (
                    <span className="rounded-full bg-[#fff7d6] px-2 py-0.5 text-xs text-[#916c00]">{sectionMissing}</span>
                  )}
                </button>
              )
            })}
          </nav>
        </aside>

        <section className="rounded-lg border border-[#dbe8c3] bg-white p-6 shadow-sm">
          <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold">{active.title}</h2>
              <p className="mt-1 text-sm text-[#657057]">{active.description}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={saveNow} disabled={isSaving || !setupToken}>
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Speichern
              </Button>
              <Button onClick={complete} disabled={isCompleting || missingRequired.length > 0 || !setupToken}>
                {isCompleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                Initialisierung abschliessen
              </Button>
            </div>
          </div>

          {message && (
            <div className="mb-5 rounded-md border border-[#dbe8c3] bg-[#f7faef] px-4 py-3 text-sm text-[#3b4035]">
              {message}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {active.fields.map((field) => {
              const value = getNestedValue(draft, field.key)
              const missing = missingRequired.includes(field.key)
              return (
                <label key={field.key} className="block">
                  <span className="mb-1 flex items-center justify-between text-sm font-medium">
                    <span>{field.label}</span>
                    {field.required && <span className="text-xs text-[#8b6500]">Pflicht</span>}
                  </span>

                  {field.type === "textarea" ? (
                    <Textarea
                      value={String(value ?? "")}
                      onChange={(event) => updateField(field, event.target.value)}
                      className={missing ? "border-[#e0aa00]" : undefined}
                    />
                  ) : field.type === "boolean" ? (
                    <div className="flex h-10 items-center rounded-md border border-input px-3">
                      <input
                        type="checkbox"
                        checked={Boolean(value)}
                        onChange={(event) => updateField(field, event.target.checked)}
                        className="h-4 w-4 accent-[#94c900]"
                      />
                    </div>
                  ) : field.type === "select" ? (
                    <select
                      value={String(value ?? "")}
                      onChange={(event) => updateField(field, event.target.value)}
                      className={[
                        "h-10 w-full rounded-md border bg-background px-3 text-sm",
                        missing ? "border-[#e0aa00]" : "border-input",
                      ].join(" ")}
                    >
                      <option value="">Bitte auswaehlen</option>
                      {field.options?.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                  ) : (
                    <Input
                      type={fieldInputType(field)}
                      value={String(value ?? "")}
                      onChange={(event) => updateField(field, event.target.value)}
                      className={missing ? "border-[#e0aa00]" : undefined}
                      placeholder={field.type === "secretRef" ? "op://Vault/Item/field" : undefined}
                    />
                  )}
                </label>
              )
            })}
          </div>

          <div className="mt-6 flex items-center justify-between border-t border-[#dbe8c3] pt-5 text-sm text-[#657057]">
            <span>{missingRequired.length === 0 ? "Alle Pflichtfelder sind gefuellt." : `${missingRequired.length} Pflichtfelder offen.`}</span>
            <span>{SETUP_SECTIONS.findIndex((section) => section.id === active.id) + 1} / {SETUP_SECTIONS.length}</span>
          </div>
        </section>
      </main>
    </div>
  )
}

export default SetupPage

